import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { PropsWithChildren, useEffect, useRef } from 'react'
import { Drawer as VaulDrawer } from 'vaul'

import { XIcon } from '../../icons'
import Button from '../atoms/button'
import Typography from '../atoms/typography'

interface Props {
  isOpen: boolean
  title?: string
  resetText?: string
  isResetLoading?: boolean
  isApplyLoading?: boolean
  isLoading?: boolean
  displayHeader?: boolean
  btnLabel?: string
  onClose: () => void
  onReset?: () => void
  onSubmit?: () => void
}

const Drawer: React.FC<PropsWithChildren<Props>> = ({
  isOpen,
  title,
  children,
  isLoading,
  displayHeader = true,
  resetText = 'Réinitialiser',
  btnLabel,
  isApplyLoading,
  isResetLoading,
  onClose,
  onReset,
  onSubmit
}) => {
  const accessibleTitle = title?.trim() ? title : 'Drawer'
  const isSubmitDisabled = isResetLoading || isApplyLoading
  const submitRef = useRef(onSubmit)
  submitRef.current = isSubmitDisabled ? undefined : onSubmit

  useEffect(() => {
    if (!isOpen) return

    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement) {
      activeElement.blur()
    }
  }, [isOpen])

  // Pressing Enter submits the drawer like its primary button, unless a focused control handles the key itself
  useEffect(() => {
    if (!isOpen) return

    const handleEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.repeat || e.isComposing || !submitRef.current) return

      const target = e.target
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.closest('button, a, textarea, select'))
      ) {
        return
      }

      e.preventDefault()
      submitRef.current()
    }

    document.addEventListener('keydown', handleEnter)

    return () => document.removeEventListener('keydown', handleEnter)
  }, [isOpen])

  return (
    <VaulDrawer.Root
      fixed
      modal
      noBodyStyles
      handleOnly
      open={isOpen}
      direction="right"
      onOpenChange={(open) => {
        if (!open && !isLoading) onClose() // Only close if drawer is closed and not loading
      }}
    >
      <VaulDrawer.Portal>
        <VaulDrawer.Overlay className="fixed inset-0 bg-black/40" />
        <VaulDrawer.Content className="bg-white flex flex-col mobile:rounded-none rounded-2xl mobile:h-full h-[95%] mobile:w-full w-[530px] fixed mobile:right-0 right-4 mobile:top-0 top-[2.5%]">
          <VaulDrawer.Title className="sr-only">{accessibleTitle}</VaulDrawer.Title>
          <VaulDrawer.Description className="sr-only">
            {`${accessibleTitle} drawer`}
          </VaulDrawer.Description>
          {displayHeader && (
            <div className="bg-white py-5 px-6 rounded-2xl sticky top-0 z-10 flex-shrink-0">
              <div className=" bg-white-active p-4 rounded-lg flex flex-row justify-between items-center gap-4">
                <Typography
                  variant="sub-heading"
                  className="ellipsis overflow-hidden whitespace-nowrap"
                >
                  {title}
                </Typography>

                <div className="cursor-pointer hidden mobile:flex tab:flex" onClick={onClose}>
                  <XIcon color={tailwindColors.grey.DEFAULT} width="20" height="20" />
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-auto pb-5 px-6">{children}</div>
          <div className="p-4 sticky bottom-0 z-10 flex-shrink-0">
            <div className="flex flex-row tab:flex-col mobile:flex-col gap-2 bg-white-active p-4 rounded-lg">
              {onReset && (
                <Button
                  className="whitespace-nowrap w-full"
                  onClick={onReset}
                  loading={isResetLoading}
                  disabled={isResetLoading || isApplyLoading}
                  variant="destructive"
                  label={resetText}
                />
              )}
              <Button
                className="whitespace-nowrap w-full"
                onClick={onSubmit}
                variant="primary"
                disabled={isSubmitDisabled}
                label={btnLabel ?? 'Appliquer'}
                loading={isApplyLoading}
              />
            </div>
          </div>
        </VaulDrawer.Content>
      </VaulDrawer.Portal>
    </VaulDrawer.Root>
  )
}

export default Drawer
